/* ErrorBoundary — last-resort guard around top-level routes.

   Why this exists
   ───────────────
   React only catches render errors at boundary components. Without one,
   a single missing import (e.g. `<Cube>` not imported from
   @phosphor-icons/react) bubbles up to the root and renders a *blank*
   page — the user loses access to navigation, profile, every other
   route. This has already bitten us three times in this session:

     - 2026-02-28 — `PencilSimple` missing in doctor/CaseDetail.jsx
     - 2026-02-28 — `ArrowsClockwise` missing in coe/PlannerCaseDetail.jsx
     - 2026-02-28 — `Cube` missing in doctor/Cases.jsx

   Wrap each top-level route with this boundary so a crash inside one
   route only takes that route down. The user keeps their session,
   nav rail, header — and gets a clear "Something went wrong" card
   with a Refresh CTA and (in dev mode) the exact error message.

   Usage:
     <ErrorBoundary routeLabel="My Cases">
         <DoctorCases />
     </ErrorBoundary>

   No need to wrap layout shells (DoctorShell, AdminLayout, CoeLayout)
   — wrap the *contents* of each `<Route element={...} />` instead so
   the layout chrome stays visible when the inner page blows up.
*/
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { WarningCircle, ArrowsClockwise, House } from "@phosphor-icons/react";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Log to the console so devs still see the full stack in production
        // builds — and so existing error-monitoring hooks (Sentry et al.)
        // continue to pick it up automatically.
        // eslint-disable-next-line no-console
        console.error(
            `[ErrorBoundary:${this.props.routeLabel || "route"}]`,
            error,
            info,
        );
    }

    handleRetry = () => {
        // Reload the current route. We deliberately do a full reload
        // rather than just resetting `this.state.error = null` because
        // many of these crashes are caused by stale code (e.g. hot-
        // reload hiccup, deploy churn). A fresh fetch fixes the symptom
        // far more reliably than letting React retry the same broken
        // render tree.
        window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;

        const isDev =
            typeof window !== "undefined" &&
            (window.location.hostname === "localhost" ||
                window.location.hostname.endsWith(".preview.emergentagent.com"));

        return (
            <div
                className="min-h-[60vh] flex items-center justify-center px-6 py-12"
                data-testid="route-error-boundary"
            >
                <div className="max-w-lg w-full rounded-2xl border border-rose-200 bg-rose-50 p-6 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-100 grid place-items-center shrink-0">
                            <WarningCircle
                                size={22}
                                weight="duotone"
                                className="text-rose-700"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-800/80">
                                Something went wrong
                            </div>
                            <h2 className="font-display text-lg font-bold text-rose-950 mt-0.5">
                                We couldn't load
                                {this.props.routeLabel
                                    ? ` ${this.props.routeLabel}`
                                    : " this page"}
                            </h2>
                            <p className="text-sm text-rose-900/80 mt-2 leading-relaxed">
                                A glitch hiccup'd on our end — your data is safe.
                                Try refreshing the page. If it keeps happening,
                                ping us at{" "}
                                <a
                                    href="mailto:support@invisique.com"
                                    className="font-semibold underline"
                                >
                                    support@invisique.com
                                </a>{" "}
                                and we'll fix it right away.
                            </p>

                            {isDev && (
                                <details
                                    className="mt-3 rounded-lg bg-white/70 border border-rose-200 p-2 text-[11px] font-mono text-rose-900"
                                    data-testid="route-error-detail"
                                >
                                    <summary className="cursor-pointer font-bold">
                                        Developer detail (visible on preview only)
                                    </summary>
                                    <div className="mt-2 break-all whitespace-pre-wrap">
                                        {String(this.state.error?.message || this.state.error)}
                                    </div>
                                </details>
                            )}

                            <div className="mt-4 flex flex-wrap gap-2">
                                <Button
                                    onClick={this.handleRetry}
                                    className="rounded-full"
                                    data-testid="route-error-retry"
                                >
                                    <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
                                    Refresh
                                </Button>
                                <Button
                                    asChild
                                    variant="outline"
                                    className="rounded-full"
                                    data-testid="route-error-home"
                                >
                                    <Link to="/">
                                        <House size={14} weight="duotone" className="mr-1.5" />
                                        Back home
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
